import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const TermsAndConditionsPage = () => {
  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Terms and Conditions - SELF Labs</title>
        <meta name="description" content="SELF Phone Terms and Conditions - Effective Date: 4/1/25" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        <div className="container mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
              📜 SELF Phone Terms and Conditions
            </h1>
            <p className="text-indigo-400 mb-8">Effective Date: 4/1/25</p>

            <div className="space-y-12">
              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">1. Introduction</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Welcome to the future of sovereign communication and AI execution.</p>
                  <p>These Terms and Conditions ("Terms") govern your use, ownership, resale, and participation in the SELF Phone ecosystem, developed by SELF Labs Inc. ("SELF Labs," "we," "us," or "our").</p>
                  <p>By purchasing, activating, using, or reselling the SELF Phone, you ("you," "your," or "user") agree to comply with these Terms.</p>
                  <p>The SELF Phone is powered by proprietary technologies, including the Harmony Signal Engine™, the Role Model AI App Store, and the Signal Economy.</p>
                </div>
              </section>

              {/* Ownership and Software License */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">2. Ownership and Software License</h2>
                <div className="prose prose-invert max-w-none">
                  <p>You own the SELF Phone hardware upon purchase.</p>
                  <p>However, you receive a limited, non-exclusive, non-transferable license to use SELF Labs' proprietary software components, including but not limited to:</p>
                  <ul>
                    <li>Harmony Signal Engine™</li>
                    <li>Role Model AI App Store™</li>
                    <li>Aura Shield™</li>
                    <li>Harmony Mesh Network Participation Layer</li>
                  </ul>
                  <p>You may not reverse engineer, modify, or redistribute any software components without express written permission.</p>
                </div>
              </section>

              {/* Data Sovereignty */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">3. Data Sovereignty and Joint Control</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs operates under a Joint Data Controller model:</p>
                  <p>You maintain primary ownership and control over your personal data generated on your SELF Phone.</p>
                  <p>SELF Labs acts as a Joint Controller solely for:</p>
                  <ul>
                    <li>Facilitating encrypted storage</li>
                    <li>Enabling optional data monetization</li>
                    <li>Operating agent task execution validations within the Signal Economy</li>
                  </ul>
                  <p>SELF Labs does not sell your personal data.</p>
                  <p>Aura Shield™ enables you to control which data is locked, shared, or monetized.</p>
                </div>
              </section>

              {/* ESSENCE Rewards */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">4. ESSENCE Rewards and Fee Structure</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Participation in the Harmony Signal Economy rewards you with ESSENCE tokens.</p>
                  <p>The reward split is as follows:</p>
                  <ul>
                    <li>98% of earnings go directly to you, the user</li>
                    <li>1% is automatically contributed to U.S. National Debt Reduction via approved Treasury offset programs</li>
                    <li>1% is contributed to the SELF Labs DAO for platform development and community governance</li>
                  </ul>
                  <p>SELF Labs collects no additional service or processing fees beyond the above.</p>
                  <p>ESSENCE is classified as a utility reward token, not an investment instrument.</p>
                </div>
              </section>

              {/* Resale Policy */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">5. Resale Policy</h2>
                <div className="prose prose-invert max-w-none">
                  <p>Resale or redistribution of the SELF Phone requires prior written approval from SELF Labs Inc.</p>
                  <p>Unauthorized resales, unauthorized distributor listings, or unauthorized private sales are prohibited and may result in:</p>
                  <ul>
                    <li>Immediate termination of Harmony Signal Engine™ software access</li>
                    <li>Deactivation of Role Model AI App Store services</li>
                    <li>Disqualification from Signal Economy participation</li>
                    <li>Voiding of warranty services</li>
                  </ul>
                  <p>Approved resellers must enter into an official Reseller Agreement and abide by SELF Labs' brand and distribution standards.</p>
                </div>
              </section>

              {/* Warranty */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">6. Warranty Disclaimer</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Phone hardware is covered under a limited manufacturer's warranty for [Insert Warranty Term: e.g., 12 months] from delivery.</p>
                  <p>Software services (Harmony Signal Engine™, Role Model AI, Signal Economy features) are provided "as is" without guarantee of uptime, performance, or continued feature availability.</p>
                  <p>SELF Labs reserves the right to issue software updates, feature improvements, or protocol changes without notice.</p>
                </div>
              </section>

              {/* Liability */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">7. Limitation of Liability</h2>
                <div className="prose prose-invert max-w-none">
                  <p>To the maximum extent permitted by law, SELF Labs is not liable for any indirect, incidental, special, consequential, or punitive damages related to:</p>
                  <ul>
                    <li>Use or misuse of the SELF Phone</li>
                    <li>Participation or non-participation in the Harmony Mesh Network</li>
                    <li>Data monetization outcomes</li>
                    <li>Resale activities</li>
                    <li>Interaction with AI agents or automated processes</li>
                  </ul>
                  <p>Your participation is voluntary and at your own risk.</p>
                </div>
              </section>

              {/* Terms Modification */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">8. Modification of Terms</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs reserves the right to update these Terms at any time.</p>
                  <p>Material changes will be communicated via your SELF Phone device and/or email.</p>
                  <p>Your continued use of the SELF Phone after any update constitutes your acceptance of the revised Terms.</p>
                </div>
              </section>

              {/* Governing Law */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">9. Governing Law</h2>
                <div className="prose prose-invert max-w-none">
                  <p>These Terms are governed by and construed under the laws of the State of [Insert State, recommend Delaware].</p>
                  <p>All disputes arising under or relating to these Terms shall be resolved through binding arbitration conducted in [Insert County/City].</p>
                </div>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">10. Contact Information</h2>
                <div className="prose prose-invert max-w-none">
                  <p>SELF Labs Inc.</p>
                  <p>📩 Email: [Insert Contact Email]</p>
                  <p>🏢 Address: 1209 N Orange St, Wilmington, Delaware</p>
                </div>
              </section>

              {/* Mesh Network */}
              <section>
                <h2 className="text-2xl font-semibold mb-4 text-indigo-400">11. Participation in the Harmony Mesh Network</h2>
                <div className="prose prose-invert max-w-none">
                  <h3 className="text-xl font-semibold mb-3 text-indigo-300">11.1 Mesh Network Participation</h3>
                  <p>By activating a SELF Phone, you may voluntarily join the Harmony Mesh Network as a:</p>
                  <ul>
                    <li>Signal relay node</li>
                    <li>Task execution node</li>
                    <li>Encrypted data routing node</li>
                  </ul>
                  <p>Participation enables the decentralized execution of Harmony Signals and AI agent tasks.</p>

                  <h3 className="text-xl font-semibold mb-3 mt-6 text-indigo-300">11.2 Network Usage Standards</h3>
                  <p>You agree to transmit only authorized, encrypted Harmony Signals.</p>
                  <p>You must not introduce malicious code, unauthorized signals, or interfere with normal Mesh operations.</p>
                  <p>Exploiting, manipulating, or disrupting Mesh operations will result in immediate suspension and forfeiture of accrued ESSENCE rewards.</p>

                  <h3 className="text-xl font-semibold mb-3 mt-6 text-indigo-300">11.3 Protocol Updates and Mesh Governance</h3>
                  <p>SELF Labs reserves the right to update Harmony Mesh Network protocols to enhance performance, security, and sovereignty.</p>
                  <p>Updates may include changes to signal formats, validation methods, encryption standards, or participation requirements.</p>
                  <p>By participating, you consent to automatic protocol upgrades and configurations deployed by SELF Labs.</p>

                  <h3 className="text-xl font-semibold mb-3 mt-6 text-indigo-300">11.4 Rewards for Mesh Participation</h3>
                  <p>Your device earns ESSENCE proportional to its validated participation in relaying, validating, and executing Harmony Signals.</p>
                  <p>Reward schedules are dynamic and adjust based on overall Mesh demand, agent task volume, and network health metrics.</p>
                  <p>Fraudulent participation (e.g., falsified relay counts) is grounds for immediate account suspension.</p>
                </div>
              </section>
            </div>
          </motion.div>

          {/* Footer */}
          <div className="mt-20 pt-12 border-t border-indigo-500/20">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">SELF Labs Inc.</h3>
                  <p className="text-gray-400">Powered by Role Model AI + Aura Protocol</p>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Legal</h3>
                  <div className="space-y-2">
                    <Link to="/terms" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Terms & Conditions
                    </Link>
                    <Link to="/privacy" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Privacy Policy
                    </Link>
                    <Link to="/harmony-litepaper" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      Harmony Litepaper
                    </Link>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-4 text-indigo-400">Contact</h3>
                  <div className="space-y-2">
                    <a href="mailto:data@rolemodel.ai" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      data@rolemodel.ai
                    </a>
                    <a href="https://x.com/selflabsinc" target="_blank" rel="noopener noreferrer" className="block text-gray-400 hover:text-indigo-400 transition-colors">
                      @selflabsinc
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-12 pt-8 border-t border-indigo-500/20 text-center text-sm text-gray-400">
                <p>&copy; {new Date().getFullYear()} SELF Labs Inc. All rights reserved.</p>
                <p className="mt-2">Protected by PureCipher's OMNISEAL™ encryption suite</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsPage; 